// scheduling/components/IDDHistoryModal.jsx
// Per-job audit timeline modal. Shows every save_draft / confirm / submit /
// clear_draft / revert / reopen event for a single (PEJobIndex, CycleName),
// with "Revert to this version" buttons on draft-state events.
//
// Submitted, revert, and clear_draft events show in the timeline for context
// but cannot themselves be reverted to (per backend rules). The revert action
// itself is allowed only when the current review is in draft/confirmed state.

import { useState, useEffect, useCallback } from 'react';
import { fetchIDDHistory } from '../services/schedData';
import { revertIDDAudit } from '../services/schedApi';

// Date-only display helper. SQL DATE columns deserialize as UTC midnight,
// which on local conversion shifts back a day; this avoids that.
function fmtDate(val) {
  if (!val) return '—';
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtDateTime(val) {
  if (!val) return '';
  return new Date(val).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
function fmtNum(v) {
  if (v == null) return '—';
  return String(Math.round(Number(v) * 100) / 100);
}

// Action → display label + class suffix. Submit failures are highlighted.
function actionDisplay(row) {
  switch (row.Action) {
    case 'save_draft': return { label: 'Saved Draft', cls: 'save_draft' };
    case 'confirm':    return { label: 'Confirmed',  cls: 'confirm' };
    case 'submit':     return {
      label: row.PEWriteSucceeded ? 'Submitted to PE' : 'Submit Failed',
      cls: row.PEWriteSucceeded ? 'submit' : 'submit-failed',
    };
    case 'clear_draft': return { label: 'Cleared Draft', cls: 'clear_draft' };
    case 'revert':     return { label: 'Reverted',  cls: 'revert' };
    case 'reopen':     return { label: 'Admin Reopened', cls: 'reopen' };
    default:           return { label: row.Action, cls: 'unknown' };
  }
}

// Whether this event is a candidate for "Revert to this version".
// Submit / revert / clear_draft / reopen are excluded — they're history
// markers, not states we restore to.
function canRevertTo(row) {
  return row.Action === 'save_draft' || row.Action === 'confirm';
}

export default function IDDHistoryModal({
  open,
  onClose,
  peJobIndex,
  cycleName,
  jobLabel,
  currentStatus,    // 'draft' | 'confirmed' | 'submitted' | 'failed' | undefined
  onRevertSuccess,  // called after a successful revert (parent reloads)
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busyAuditId, setBusyAuditId] = useState(null);

  const load = useCallback(async () => {
    if (!peJobIndex || !cycleName) return;
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchIDDHistory(peJobIndex, cycleName);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [peJobIndex, cycleName]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Revert is blocked when the current review is in submitted/failed status.
  // (Backend also enforces this.) Submitted is final; reopen via admin first.
  const revertBlocked = currentStatus === 'submitted' || currentStatus === 'failed';

  async function handleRevert(row) {
    if (revertBlocked) return;
    if (!window.confirm(
      `Restore values from this point?\n\n`
      + `${actionDisplay(row).label} on ${fmtDateTime(row.ChangedAt)}.\n\n`
      + `Any newer edits to this job will be replaced.`
    )) return;
    setBusyAuditId(row.AuditId);
    try {
      await revertIDDAudit(row.AuditId);
      // Pass the audit row to the parent so it can patch state in place
      // without a full table reload (mirrors clearDraft / undoConfirm
      // surgical pattern).
      if (onRevertSuccess) await onRevertSuccess(row);
      await load();
    } catch (e) {
      alert('Revert failed: ' + e.message);
    } finally {
      setBusyAuditId(null);
    }
  }

  if (!open) return null;

  return (
    <div className="idd-history-modal-overlay" onClick={onClose}>
      <div className="idd-history-modal" onClick={ev => ev.stopPropagation()}>
        <div className="idd-history-modal-head">
          <div>
            <div className="idd-history-modal-title">Edit History</div>
            {jobLabel && <div className="idd-history-modal-subtitle">{jobLabel}</div>}
          </div>
          <button className="idd-history-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="idd-history-modal-body">
          {loading && <div className="idd-history-empty">Loading…</div>}
          {err && <div className="idd-history-empty idd-history-empty--err">Error: {err}</div>}
          {!loading && !err && rows.length === 0 && (
            <div className="idd-history-empty">No history yet for this job in this cycle.</div>
          )}
          {!loading && !err && rows.length > 0 && (
            <div className="idd-history-list">
              {rows.map(row => {
                const { label, cls } = actionDisplay(row);
                const startChanged = row.OldStartDeadline !== row.NewStartDeadline;
                const endChanged = row.OldDeadline !== row.NewDeadline;
                const budgetChanged = row.OldBudgetHours !== row.NewBudgetHours;
                const remainingChanged = row.OldRemainingHrs !== row.NewRemainingHrs;
                const statusChanged = row.OldStatus !== row.NewStatus;
                let taskDiff = null;
                try {
                  if (row.TaskChanges) taskDiff = JSON.parse(row.TaskChanges);
                } catch (_) { taskDiff = null; }
                const showRevert = canRevertTo(row) && !revertBlocked;
                return (
                  <div key={row.AuditId} className="idd-history-event">
                    <div className="idd-history-event-head">
                      <span className={`idd-history-action-chip idd-history-action--${cls}`}>{label}</span>
                      {row.Source && row.Source !== 'manual' && (
                        <span className="idd-history-source-chip">{row.Source}</span>
                      )}
                      <span className="idd-history-meta">
                        {row.ChangedByName || row.ChangedBy} · {fmtDateTime(row.ChangedAt)}
                      </span>
                      {showRevert && (
                        <button
                          className="idd-history-revert-btn"
                          onClick={() => handleRevert(row)}
                          disabled={busyAuditId != null}
                          title="Restore the IDDReviews state to this point. Any newer edits will be overwritten."
                        >
                          {busyAuditId === row.AuditId ? 'Reverting…' : 'Revert to this version'}
                        </button>
                      )}
                    </div>

                    {row.Action === 'submit' && row.PEWriteSucceeded === false && row.PEWriteError && (
                      <div className="idd-history-error">PE write failed: {row.PEWriteError}</div>
                    )}

                    <div className="idd-history-diffs">
                      {statusChanged && (
                        <div className="idd-history-diff">Status: <strong>{row.OldStatus || '—'}</strong> → <strong>{row.NewStatus || '—'}</strong></div>
                      )}
                      {startChanged && (
                        <div className="idd-history-diff">IDD Start: {fmtDate(row.OldStartDeadline)} → <strong>{fmtDate(row.NewStartDeadline)}</strong></div>
                      )}
                      {endChanged && (
                        <div className="idd-history-diff">IDD End: {fmtDate(row.OldDeadline)} → <strong>{fmtDate(row.NewDeadline)}</strong></div>
                      )}
                      {budgetChanged && (
                        <div className="idd-history-diff">Budget: {fmtNum(row.OldBudgetHours)} → <strong>{fmtNum(row.NewBudgetHours)}</strong>h</div>
                      )}
                      {remainingChanged && (
                        <div className="idd-history-diff">Remaining: {fmtNum(row.OldRemainingHrs)} → <strong>{fmtNum(row.NewRemainingHrs)}</strong>h</div>
                      )}
                      {taskDiff && taskDiff.length > 0 && (
                        <div className="idd-history-task-diffs">
                          <div className="idd-history-task-diffs-label">Tasks ({taskDiff.length}):</div>
                          {taskDiff.slice(0, 6).map((t, i) => (
                            <div key={i} className="idd-history-diff idd-history-diff--task">
                              <span className="idd-history-task-name">{t.TaskSubject}</span>
                              <span>
                                {fmtNum(t.OldRemainingHrs)} → <strong>{fmtNum(t.NewRemainingHrs)}</strong>h
                              </span>
                            </div>
                          ))}
                          {taskDiff.length > 6 && (
                            <div className="idd-history-diff idd-history-diff--more">
                              +{taskDiff.length - 6} more
                            </div>
                          )}
                        </div>
                      )}
                      {row.Notes && (
                        <div className="idd-history-notes">{row.Notes}</div>
                      )}
                      {/* No actual diffs to show — rare, but keep layout clean */}
                      {!statusChanged && !startChanged && !endChanged && !budgetChanged && !remainingChanged && !taskDiff?.length && !row.Notes && (
                        <div className="idd-history-diff idd-history-diff--none">(no value changes captured)</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {revertBlocked && (
          <div className="idd-history-modal-footer-warning">
            This review is currently {currentStatus} — revert is disabled. Ask an admin to reopen first.
          </div>
        )}
      </div>
    </div>
  );
}
